'use client'

import { useEffect } from 'react'
import { getPlaySettings } from '@/lib/phishtrainer/play-settings'

/**
 * Publishes the workspace brand colour as `--pt-brand` for the player portal.
 *
 * WHY THIS EXISTS. /play/layout.tsx already paints its header accent bar with
 * `var(--pt-brand, transparent)` and its comment claims the admin colour selector sets
 * it. Nothing in this route tree ever did: the only assignment lives in
 * app/phishtrainer/play/_player/contexts/SettingsContext.tsx, mounted solely by the
 * ADMIN-side PlayerShell. The standalone player layout mounts QueryProvider,
 * PlayerAuthProvider and PlayerThemeProvider — never SettingsProvider — so the variable
 * was undefined here and the bar fell back to transparent. Changing the colour in
 * PhishTrainer Settings (labelled "Accent colour" since the P0 rename — it was
 * "Primary colour", which is what made it sound like it set --primary) did nothing
 * visible in the portal it was meant for.
 *
 * SCOPE. One CSS variable, consumed by the accent bar that already exists. It does not
 * repaint buttons and does not touch the learner's own light/dark and palette
 * selection — those are learner-owned, they work, and overriding them to show a brand
 * colour would remove a working control.
 *
 * Renders nothing; it is a side-effect component so the layout can stay a server
 * component around it.
 *
 * MEASURED: mounting this alongside PlayerBrandLogo costs NO extra request. Both go
 * through the memoised getPlaySettings, and /play issues exactly one
 * GET /api/phishtrainer/settings with both mounted.
 *
 * An earlier revision of this comment retracted that claim after seeing three requests on
 * /play/game. The retraction was wrong: two of those three come from PlayerShell and
 * SettingsContext, which /play/game mounts and /play does not, and which used to fetch
 * the endpoint directly instead of through the memo. The original claim was correct.
 */

/**
 * This value is written straight onto document.documentElement.style, so anything that
 * is not recognisably a colour is dropped rather than injected.
 *
 * The WRITE path is now validated — BrandingSectionDto applies @Matches(HEX_COLOR_RE),
 * so new values are hex-only. This regex stays deliberately WIDER than that. It guards a
 * READ, and the column predates the validation: rows written before it can still hold
 * rgb()/hsl()/named colours, and narrowing here would blank the accent bar for those
 * tenants rather than fix anything. Read tolerantly, write strictly.
 */
const COLOR_RE =
  /^(#[0-9a-f]{3,8}|rgba?\([^()]*\)|hsla?\([^()]*\)|oklch\([^()]*\)|oklab\([^()]*\)|[a-z]{3,20})$/i

export function PlayerBrandAccent() {
  useEffect(() => {
    let cancelled = false

    getPlaySettings()
      .then((j) => {
        if (cancelled) return
        const raw: unknown = j?.data?.branding?.primaryColor
        if (typeof raw !== 'string') return

        const value = raw.trim()
        if (!value || !COLOR_RE.test(value)) return

        document.documentElement.style.setProperty('--pt-brand', value)
      })
      .catch(() => {
        // Leave the bar on its `transparent` fallback.
      })

    return () => {
      cancelled = true
    }
  }, [])

  return null
}
